import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Package, TrendingUp, AlertTriangle, CheckCircle, 
  DollarSign, BarChart3, RefreshCw, Search, Download 
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ZOBOX_BLUE = '#2b3380';

const PartsInventory = () => {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('total_used'); // total_used, damage_rate, avg_price
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    fetchInventory();
  }, []);

  const fetchInventory = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/trc/parts/inventory`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setInventory(response.data.inventory);
      }
    } catch (error) {
      console.error('Error fetching parts inventory:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const getSortedInventory = () => {
    let filtered = inventory.filter(part =>
      part.part_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      part.supplier.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return filtered.sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });
  };

  const summary = inventory.length > 0 ? {
    totalParts: inventory.length,
    totalConsumed: inventory.reduce((sum, p) => sum + p.consumed, 0),
    totalReturned: inventory.reduce((sum, p) => sum + p.returned, 0),
    totalDamaged: inventory.reduce((sum, p) => sum + p.damaged, 0),
    avgDamageRate: (inventory.reduce((sum, p) => sum + p.damage_rate, 0) / inventory.length).toFixed(2)
  } : null;

  const exportToCSV = () => {
    const headers = ['Part Code', 'Supplier', 'Consumed', 'Returned', 'Damaged', 'Total Used', 'Avg Price', 'Damage Rate (%)'];
    const rows = getSortedInventory().map(part => [
      part.part_code,
      part.supplier,
      part.consumed,
      part.returned,
      part.damaged,
      part.total_used,
      part.avg_price,
      part.damage_rate
    ]);

    let csv = headers.join(',') + '\n';
    csv += rows.map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `parts-inventory-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: ZOBOX_BLUE }}></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center" style={{ color: ZOBOX_BLUE }}>
            <Package className="w-8 h-8 mr-3" />
            Parts Inventory Management
          </h1>
          <p className="text-gray-600 mt-1">
            Track parts consumption, returns, and damage rates
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={exportToCSV}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </button>
          <button
            onClick={fetchInventory}
            className="flex items-center px-4 py-2 text-white rounded-lg hover:opacity-90 transition-colors"
            style={{ backgroundColor: ZOBOX_BLUE }}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-md border-l-4" style={{ borderColor: ZOBOX_BLUE }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Total Parts</p>
                <p className="text-3xl font-bold mt-2" style={{ color: ZOBOX_BLUE }}>
                  {summary.totalParts}
                </p>
              </div>
              <Package className="w-10 h-10" style={{ color: ZOBOX_BLUE }} />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Consumed</p>
                <p className="text-3xl font-bold text-green-600 mt-2">
                  {summary.totalConsumed}
                </p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Returned</p>
                <p className="text-3xl font-bold text-blue-600 mt-2">
                  {summary.totalReturned}
                </p>
              </div>
              <TrendingUp className="w-10 h-10 text-blue-600" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-red-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Damaged</p>
                <p className="text-3xl font-bold text-red-600 mt-2">
                  {summary.totalDamaged}
                </p>
              </div>
              <AlertTriangle className="w-10 h-10 text-red-600" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-orange-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Avg Damage Rate</p>
                <p className="text-3xl font-bold text-orange-600 mt-2">
                  {summary.avgDamageRate}%
                </p>
              </div>
              <BarChart3 className="w-10 h-10 text-orange-600" />
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-lg shadow-md">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by part code or supplier..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2"
              style={{ focusRingColor: ZOBOX_BLUE }}
            />
          </div>
          <select
            value={sortBy}
            onChange={(e) => handleSort(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2"
            style={{ focusRingColor: ZOBOX_BLUE }}
          >
            <option value="total_used">Sort by Most Used</option>
            <option value="damage_rate">Sort by Damage Rate</option>
            <option value="avg_price">Sort by Price</option>
            <option value="consumed">Sort by Consumed</option>
          </select>
        </div>
      </div>

      {/* Inventory Table */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Part Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Supplier
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Consumed
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Returned
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Damaged
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Total Used
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Avg Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Damage Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {getSortedInventory().map((part, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    {part.part_code}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {part.supplier || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    <span className="font-semibold text-green-600">{part.consumed}</span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    <span className="font-semibold text-blue-600">{part.returned}</span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    <span className="font-semibold text-red-600">{part.damaged}</span>
                  </td>
                  <td className="px-6 py-4 text-sm font-bold text-gray-900">
                    {part.total_used}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    <span className="flex items-center">
                      <DollarSign className="w-4 h-4 mr-1" />
                      {part.avg_price > 0 ? part.avg_price.toFixed(2) : '-'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="w-20 bg-gray-200 rounded-full h-2 mr-2">
                        <div
                          className={`h-2 rounded-full ${
                            part.damage_rate > 20 ? 'bg-red-500' :
                            part.damage_rate > 10 ? 'bg-orange-500' :
                            'bg-green-500'
                          }`}
                          style={{ width: `${Math.min(part.damage_rate, 100)}%` }}
                        />
                      </div>
                      <span className={`text-sm font-semibold ${
                        part.damage_rate > 20 ? 'text-red-600' :
                        part.damage_rate > 10 ? 'text-orange-600' :
                        'text-green-600'
                      }`}>
                        {part.damage_rate}%
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {part.damage_rate > 20 ? (
                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                        High Damage
                      </span>
                    ) : part.damage_rate > 10 ? (
                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-800">
                        Monitor
                      </span>
                    ) : (
                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                        Good
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {inventory.length === 0 && (
          <div className="p-12 text-center text-gray-500">
            <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg">No parts consumption data available</p>
            <p className="text-sm mt-2">Parts data will appear as repairs are completed</p>
          </div>
        )}
      </div>

      {/* High Damage Rate Alert */}
      {inventory.filter(p => p.damage_rate > 20).length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertTriangle className="w-6 h-6 text-red-600 mt-1" />
            <div className="ml-3">
              <h3 className="text-lg font-bold text-red-800">High Damage Rate Alert</h3>
              <p className="text-red-700 mt-1">
                {inventory.filter(p => p.damage_rate > 20).length} parts have damage rates above 20%.
                Review handling procedures and supplier quality.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartsInventory;
